import React, { useState } from "react";
import { Mail, Send, Layout, Search, Lock, Sparkles, ArrowRight, BarChart3 } from "lucide-react";
import MailingListsView from "./MailingListsView";
import MailingListDetail from "./MailingListDetail";
import GlobalSearchView from "./GlobalSearchView";
import CampaignsView from "./CampaignsView";
import CampaignAnalytics from "./CampaignAnalytics";

/**
 * DirectMailDashboard - Main container for the Direct Mail feature
 * Provides tab navigation between Mailing Lists, Search, Campaigns, and Templates
 * Requires Ultra or Enterprise subscription
 */
const DirectMailDashboard = ({ userEmail, userMeta, featureFlags = {} }) => {
  const [activeTab, setActiveTab] = useState("lists");
  const [selectedList, setSelectedList] = useState(null);

  // Check if user has required tier (ultra, enterprise, rp_ultra)
  const hasAccess = featureFlags.isRpUltra || featureFlags.isUltra || featureFlags.isEnterprise || featureFlags.hasDirectMail;

  // Upgrade prompt for non-eligible users
  if (!hasAccess) {
    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden bg-white dark:bg-slate-950">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-lg">
            {/* Lock icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Lock className="w-10 h-10 text-white" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold dark:text-white text-slate-900 mb-3">
              Direct Mail Requires Ultra
            </h1>

            {/* Description */}
            <p className="text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
              Access our database of <strong className="text-slate-700 dark:text-slate-300">61,490+ Vietnamese homeowners</strong> in Houston and Dallas.
              Send targeted USPS postcards directly from your dashboard.
            </p>

            {/* Features list */}
            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-5 mb-6 text-left">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <span className="font-semibold dark:text-white text-slate-900">Included with Ultra:</span>
              </div>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  47,534 contacts in Harris County (Houston)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  13,956 contacts in Dallas County (DFW)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Search by name, city, ZIP code
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Send USPS postcards starting at $0.70/each
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Export to CSV for external campaigns
                </li>
              </ul>
            </div>

            {/* Current tier info */}
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">
              Your current plan: <span className="font-medium">{userMeta?.membership || "Free"}</span>
            </p>

            {/* Upgrade button */}
            <button
              onClick={() => window.location.href = "/pricing"}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity shadow-lg shadow-indigo-500/25"
            >
              Upgrade to Ultra
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tab button component
  const TabButton = ({ id, icon: Icon, label, isActive }) => (
    <button
      onClick={() => {
        setActiveTab(id);
        setSelectedList(null); // Clear selection when switching tabs
      }}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        isActive
          ? "bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400"
          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-white dark:bg-slate-950">
      {/* Header with tabs */}
      <div className="border-b border-slate-200 dark:border-slate-800 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold dark:text-white text-slate-900">
                Direct Mail
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Manage mailing lists and send USPS postcards
              </p>
            </div>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-2">
          <TabButton
            id="lists"
            icon={Mail}
            label="Mailing Lists"
            isActive={activeTab === "lists"}
          />
          <TabButton
            id="search"
            icon={Search}
            label="Search"
            isActive={activeTab === "search"}
          />
          <TabButton
            id="campaigns"
            icon={Send}
            label="Campaigns"
            isActive={activeTab === "campaigns"}
          />
          <TabButton
            id="templates"
            icon={Layout}
            label="Templates"
            isActive={activeTab === "templates"}
          />
          <TabButton
            id="analytics"
            icon={BarChart3}
            label="Analytics"
            isActive={activeTab === "analytics"}
          />
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "lists" && !selectedList && (
          <MailingListsView
            onSelectList={setSelectedList}
            userEmail={userEmail}
            userMeta={userMeta}
          />
        )}

        {activeTab === "lists" && selectedList && (
          <MailingListDetail
            listId={selectedList.id || selectedList.list_id}
            listName={selectedList.name || selectedList.list_name}
            onBack={() => setSelectedList(null)}
            userEmail={userEmail}
            userMeta={userMeta}
          />
        )}

        {activeTab === "search" && (
          <GlobalSearchView
            userEmail={userEmail}
            userMeta={userMeta}
          />
        )}

        {activeTab === "campaigns" && (
          <CampaignsView
            userEmail={userEmail}
            userMeta={userMeta}
          />
        )}

        {activeTab === "templates" && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Layout className="w-8 h-8 text-slate-400" />
              </div>
              <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                Templates
              </h2>
              <p className="dark:text-slate-400 text-slate-500">
                Create and manage postcard templates for your mailings. Coming soon.
              </p>
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <CampaignAnalytics
            userEmail={userEmail}
            userMeta={userMeta}
          />
        )}
      </div>
    </div>
  );
};

export default DirectMailDashboard;
